import type { ZoneKindInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input } from 'antd';
import { useEffect } from 'react';
import type { ZoneKind } from './reference-data.api';

interface ZoneKindFormDrawerProps {
  open: boolean;
  /** Present to edit that row; absent to create a new one. */
  editing?: ZoneKind;
  error: string | null;
  submitting: boolean;
  onSubmit: (input: ZoneKindInput) => Promise<boolean>;
  onClose: () => void;
}

/** One drawer for both create and edit — same shape as TrailerTypeFormDrawer. */
export function ZoneKindFormDrawer({ open, editing, error, submitting, onSubmit, onClose }: ZoneKindFormDrawerProps) {
  const [form] = Form.useForm<ZoneKindInput>();

  useEffect(() => {
    if (open) form.setFieldsValue(editing ?? { code: '', name: '' });
  }, [open, editing, form]);

  async function submit(values: ZoneKindInput) {
    const saved = await onSubmit(values);
    if (saved) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit zone kind' : 'New zone kind'} placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<ZoneKindInput> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create zone kind'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
